import React, { useState, useEffect, useCallback } from "react";
import { App, Card, InputNumber, Button, Tag, Divider } from "antd";
import { useDispatch } from "react-redux";
import { savePricingConfig, updatePricingConfig } from "../../store/slice/pricing";
import { DEMAND_TIER_MULTIPLIERS } from "./constants";

const FEE_TYPES = [
  {
    key: "joining",
    label: "Joining Fee (₹)",
    stateKey: "joining",
    step: 10,
  },
  {
    key: "proMonthly",
    label: "Pro Tier Monthly (₹)",
    stateKey: "proMonthly",
    step: 10,
  },
  {
    key: "premiumMonthly",
    label: "Premium Tier Monthly (₹)",
    stateKey: "premiumMonthly",
    step: 10,
  },
  {
    key: "featuredWeekly",
    label: "Featured Placement Weekly (₹)",
    stateKey: "featuredWeekly",
    step: 5,
  },
];

const FIELD_MAPPING = {
  joining: "baseJoiningFee",
  proMonthly: "baseProMonthlyFee",
  premiumMonthly: "basePremiumMonthlyFee",
  featuredWeekly: "baseFeaturedWeeklyCost",
};

function PricingSettingCard({ locality, config, onSave }) {
  const [formValues, setFormValues] = useState({
    joining: 0,
    proMonthly: 0,
    premiumMonthly: 0,
    featuredWeekly: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { message } = App.useApp();
  const dispatch = useDispatch();

  useEffect(() => {
    if (config) {
      setFormValues({
        joining: config.baseJoiningFee || 0,
        proMonthly: config.baseProMonthlyFee || 0,
        premiumMonthly: config.basePremiumMonthlyFee || 0,
        featuredWeekly: config.baseFeaturedWeeklyCost || 0,
      });
    }
  }, [config]);

  const handleFieldChange = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validateForm = useCallback(() => {
    const allPositive = Object.values(formValues).every((v) => v > 0);
    if (!allPositive) {
      message.error("All pricing values must be greater than 0");
      return false;
    }
    return true;
  }, [formValues, message]);

  const buildPayload = useCallback(() => {
    return {
      baseJoiningFee: formValues.joining,
      baseProMonthlyFee: formValues.proMonthly,
      basePremiumMonthlyFee: formValues.premiumMonthly,
      baseFeaturedWeeklyCost: formValues.featuredWeekly,
      localityType: locality.key,
    };
  }, [formValues, locality.key]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    const thunkCreator = config ? updatePricingConfig : savePricingConfig;
    const payload = buildPayload();

    try {
      const resultAction = await dispatch(thunkCreator(payload));

      if (thunkCreator.fulfilled.match(resultAction)) {
        message.success(`Pricing for ${locality.label} updated!`);
        onSave(locality.label);
      } else {
        message.error(resultAction.error?.message ?? "Failed to save pricing");
      }
    } finally {
      setIsLoading(false);
    }
  }, [validateForm, config, dispatch, buildPayload, locality.label, onSave, message]);

  const borderColor = {
    red: "#ff4d4f",
    blue: "#1890ff",
    purple: "#722ed1",
    green: "#52c41a",
  }[locality.color];

  return (
    <Card
      size="small"
      className="border-2"
      style={{ borderColor }}
      title={
        <div className="flex items-center gap-2">
          <Tag color={locality.color} style={{ marginRight: 0 }}>
            {locality.label}
          </Tag>
        </div>
      }
    >
      <p className="text-xs text-gray-600 mb-4">{locality.description}</p>

      <div className="space-y-4">
        {FEE_TYPES.map((feeType) => (
          <FeeInput
            key={feeType.key}
            feeType={feeType}
            value={formValues[feeType.stateKey]}
            onChange={(val) => handleFieldChange(feeType.stateKey, val)}
          />
        ))}

        <Button
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          block
          className="bg-orange-500 hover:bg-orange-600 mt-4"
        >
          {isLoading ? "Saving..." : "Save Pricing"}
        </Button>
      </div>
    </Card>
  );
}

function FeeInput({ feeType, value, onChange }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {feeType.label}
        </label>
        <InputNumber
          value={value}
          onChange={onChange}
          className="w-full"
          min={1}
          step={feeType.step}
        />
        <MultiplierPreview value={value} />
      </div>
      <Divider style={{ margin: "12px 0" }} />
    </>
  );
}

function MultiplierPreview({ value }) {
  const high = Math.ceil(value * DEMAND_TIER_MULTIPLIERS.HIGH);
  const standard = value;
  const low = Math.ceil(value * DEMAND_TIER_MULTIPLIERS.LOW);

  return (
    <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
      <p>
        High: ₹{high} | Standard: ₹{standard} | Low: ₹{low}
      </p>
    </div>
  );
}

export default PricingSettingCard;
